import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { ArrowLeft, X, Loader2, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useAuthUser } from "@/lib/auth";
import { isInWindow } from "@/lib/gameStatus";
import { useServerGameStatus, statusLabel } from "@/lib/serverGameStatus";
import { useAppSettings } from "@/lib/settings";
import { placeBetSlip, newIdempotencyKey } from "@/lib/bet-client";
import { usePageLoadMetric } from "@/lib/diagnostics/usePageLoadMetric";


export const Route = createFileRoute("/_authenticated/play/$gameId")({
  component: PlayPage,
});

type BetTab = "jodi" | "haruf" | "crossing";

function pad2(n: number) {
  return n.toString().padStart(2, "0");
}

function PlayPage() {
  const { gameId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthUser();
  const [tab, setTab] = useState<BetTab>("jodi");
  const [submitting, setSubmitting] = useState(false);
  // Stable per-slip idempotency key: regenerated only after a successful bet.
  const [idemKey, setIdemKey] = useState<string>(() => newIdempotencyKey());


  // Jodi
  const [jodiSelected, setJodiSelected] = useState<Set<number>>(new Set());
  const [jodiAmounts, setJodiAmounts] = useState<Record<number, string>>({});

  // Haruf - per-digit amounts
  const [andarAmounts, setAndarAmounts] = useState<Record<number, string>>({});
  const [baharAmounts, setBaharAmounts] = useState<Record<number, string>>({});

  // Crossing
  const [crossDigits, setCrossDigits] = useState<Set<number>>(new Set());
  const [withJoda, setWithJoda] = useState<boolean>(false);
  const [crossAmount, setCrossAmount] = useState<string>("");

  const { data: game } = useQuery({
    queryKey: ["game", gameId],
    queryFn: async () => {
      const { data } = await supabase.from("games").select("*").eq("id", gameId).single();
      return data;
    },
  });

  usePageLoadMetric("bet_screen_ready", !game);

  const today = new Date().toISOString().slice(0, 10);

  // Per-cycle bets by this user in THIS game (after last declared result).
  // Used to enforce admin-configured per-game selection limits.
  const { data: cycleBets = [] } = useQuery<{ number: number; amount: number }[]>({
    queryKey: ["bets_cycle_full", gameId, user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data: lastResult } = await supabase
        .from("results")
        .select("created_at")
        .eq("game_id", gameId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      let q = supabase
        .from("bets")
        .select("number,amount")
        .eq("user_id", user!.id)
        .eq("game_id", gameId)
        .neq("status", "reversed");
      if (lastResult?.created_at) q = q.gt("created_at", lastResult.created_at);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as { number: number; amount: number }[];
    },
    refetchOnWindowFocus: true,
  });

  // Daily bets (IST calendar date) by this user in THIS game.
  // Used to enforce the cumulative daily per-number amount limit
  // for the active Rule A / Rule B maximum.
  const istDayStartIso = useMemo(() => {
    const n = new Date();
    const istMs = n.getTime() + 5.5 * 60 * 60 * 1000;
    const ist = new Date(istMs);
    const midnightIstAsUtc = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate());
    return new Date(midnightIstAsUtc - 5.5 * 60 * 60 * 1000).toISOString();
  }, []);
  const { data: dailyBets = [] } = useQuery<{ number: number; amount: number }[]>({
    queryKey: ["bets_daily_ist", gameId, user?.id, istDayStartIso],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bets")
        .select("number,amount,status,created_at")
        .eq("user_id", user!.id)
        .eq("game_id", gameId)
        .gte("created_at", istDayStartIso);
      if (error) throw error;
      return ((data ?? []) as any[])
        .filter((b) => !["reversed", "cancelled", "refunded"].includes(String(b.status)))
        .map((b) => ({ number: b.number, amount: Number(b.amount) }));
    },
    refetchOnWindowFocus: true,
  });

  const todaysNumberTotals = useMemo<Record<number, number>>(() => {
    const map: Record<number, number> = {};
    for (const r of cycleBets) map[r.number] = (map[r.number] ?? 0) + Number(r.amount ?? 0);
    return map;
  }, [cycleBets]);

  const dailyNumberTotals = useMemo<Record<number, number>>(() => {
    const map: Record<number, number> = {};
    for (const r of dailyBets) map[r.number] = (map[r.number] ?? 0) + Number(r.amount ?? 0);
    return map;
  }, [dailyBets]);




  const { data: settings } = useAppSettings();
  const numberLimits = settings?.number_limits ?? {};


  // Server-driven status. Refreshes every 15s and on visibility/focus.
  const { data: serverStatus, refetch: refetchStatus } = useServerGameStatus(gameId);
  const status = (serverStatus?.status ?? "OPEN") as "OPEN" | "CLOSED" | "UPCOMING";
  const canBet = !!serverStatus?.is_betting_open;
  const closedMessage = statusLabel(status) || "Betting Closed.";

  // Retain a client tick only for rule-A/rule-B slab UI display (per-number min/max),
  // not for open/close decisions. Slab min/max is also re-enforced by the server RPC.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(id);
  }, []);

  function inWin(s?: string | null, e?: string | null): boolean {
    return isInWindow(s, e, now);
  }
  const activeRule = useMemo(() => {
    if (!game) return null;
    const hasA = game.rule_a_start && game.rule_a_end;
    const hasB = game.rule_b_start && game.rule_b_end;
    if (hasA && inWin(game.rule_a_start, game.rule_a_end)) return { label: "A", min: Number(game.rule_a_min), max: Number(game.rule_a_max) };
    if (hasB && inWin(game.rule_b_start, game.rule_b_end)) return { label: "B", min: Number(game.rule_b_min), max: Number(game.rule_b_max) };
    // No slab configured for the current time — no bet-amount limit.
    // The app must not apply any fallback min/max; only admin-configured
    // slab rules and per-number limits are enforced.
    return { label: "none", min: 0, max: 0 };
  }, [game, now]);
  const ruleBlocked = false;

  // ---- Compute entries per source ----
  const jodiEntries = useMemo(() => {
    const out: { number: number; amount: number }[] = [];
    for (const n of jodiSelected) {
      const v = parseFloat(jodiAmounts[n] ?? "");
      if (!isNaN(v) && v > 0) out.push({ number: n, amount: v });
    }
    return out;
  }, [jodiSelected, jodiAmounts]);

  const harufEntries = useMemo(() => {
    const out: { number: number; amount: number }[] = [];
    for (let a = 0; a <= 9; a++) {
      const v = parseFloat(andarAmounts[a] ?? "");
      if (isNaN(v) || v <= 0) continue;
      for (let b = 0; b <= 9; b++) out.push({ number: a * 10 + b, amount: v });
    }
    for (let b = 0; b <= 9; b++) {
      const v = parseFloat(baharAmounts[b] ?? "");
      if (isNaN(v) || v <= 0) continue;
      for (let a = 0; a <= 9; a++) out.push({ number: a * 10 + b, amount: v });
    }
    return out;
  }, [andarAmounts, baharAmounts]);

  const crossingJodis = useMemo(() => {
    const digits = Array.from(crossDigits).sort((a, b) => a - b);
    const set = new Set<number>();
    for (const a of digits) {
      for (const b of digits) {
        if (a === b && !withJoda) continue;
        set.add(a * 10 + b);
      }
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [crossDigits, withJoda]);

  const crossingEntries = useMemo(() => {
    const v = parseFloat(crossAmount);
    if (isNaN(v) || v <= 0) return [];
    return crossingJodis.map((n) => ({ number: n, amount: v }));
  }, [crossingJodis, crossAmount]);

  const allEntries = useMemo(
    () => [...jodiEntries, ...harufEntries, ...crossingEntries],
    [jodiEntries, harufEntries, crossingEntries],
  );
  const total = allEntries.reduce((s, e) => s + e.amount, 0);

  // ---- Selection helpers ----
  function toggleJodi(n: number) {
    if (!canBet) return toast.error(closedMessage);
    setJodiSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(n)) {
        nx.delete(n);
        setJodiAmounts((a) => { const na = { ...a }; delete na[n]; return na; });
      } else {
        nx.add(n);
      }
      return nx;
    });
  }
  function setJodiAmount(n: number, v: string) {
    if (v !== "" && !/^\d*\.?\d{0,2}$/.test(v)) return;
    setJodiAmounts((p) => ({ ...p, [n]: v }));
  }
  function toggleDigit(set: Set<number>, setter: (s: Set<number>) => void, d: number) {
    const nx = new Set(set);
    if (nx.has(d)) nx.delete(d); else nx.add(d);
    setter(nx);
  }
  function toggleCross(d: number) {
    if (!canBet) return toast.error(closedMessage);
    setCrossDigits((prev) => {
      const nx = new Set(prev);
      if (nx.has(d)) nx.delete(d); else nx.add(d);
      return nx;
    });
  }

  function clearAll() {
    setJodiSelected(new Set()); setJodiAmounts({});
    setAndarAmounts({}); setBaharAmounts({});
    setCrossDigits(new Set()); setWithJoda(false); setCrossAmount("");
  }

  function validate(): string | null {
    if (!canBet) return closedMessage;
    if (ruleBlocked) return "Betting is not allowed at this time.";
    if (allEntries.length === 0) return "Please select at least one number.";

    if (activeRule && activeRule.label !== "none") {
      for (const e of allEntries) {
        if (e.amount < activeRule.min) return `Minimum ₹${activeRule.min} per number right now.`;
        if (e.amount > activeRule.max) return `Maximum ₹${activeRule.max} per number right now.`;
      }

      // Cumulative daily per-number check against active Rule max.
      // Scope: same user + same game + same number + same IST calendar date.
      const perNumberNewDaily = new Map<number, number>();
      for (const e of allEntries) {
        perNumberNewDaily.set(e.number, (perNumberNewDaily.get(e.number) ?? 0) + e.amount);
      }
      for (const [num, addAmt] of perNumberNewDaily) {
        const already = dailyNumberTotals[num] ?? 0;
        if (already + addAmt > activeRule.max) {
          const remaining = Math.max(activeRule.max - already, 0);
          return `Daily limit reached for this number. Remaining limit: ₹${remaining}`;
        }
      }
    }




    // Per-number limit (admin-configured). Scoped per game + per day.
    // Sum this request's amounts per number and reject if the user's
    // today total in THIS game + new amount would exceed the ceiling.
    const perNumberNew = new Map<number, number>();
    for (const e of allEntries) {
      perNumberNew.set(e.number, (perNumberNew.get(e.number) ?? 0) + e.amount);
    }
    for (const [num, addAmt] of perNumberNew) {
      const raw = numberLimits[String(num)];
      const lim = typeof raw === "number" ? raw : Number(raw);
      if (!lim || lim <= 0) continue;
      const already = todaysNumberTotals[num] ?? 0;
      const remainingLim = Math.max(lim - already, 0);
      if (addAmt > remainingLim) {
        return `Maximum limit reached for this number. Remaining limit: ₹${remainingLim}`;
      }
    }

    // Per-game "Maximum Numbers Per User" (unique numbers this cycle).
    const maxUnique = Number((game as any)?.max_unique_numbers ?? 0);
    if (maxUnique > 0) {
      const existingUniq = new Set<number>(cycleBets.map((b) => b.number));
      const combined = new Set<number>(existingUniq);
      for (const e of allEntries) combined.add(e.number);
      if (combined.size > maxUnique) {
        return "Maximum number selection limit reached.";
      }
    }

    // Per-game "Maximum Numbers With Same Amount Per User" (per amount bucket).
    const maxSame = Number((game as any)?.max_same_amount_numbers ?? 0);
    if (maxSame > 0) {
      const perAmt = new Map<number, Set<number>>();
      for (const b of cycleBets) {
        const key = Number(b.amount);
        if (!perAmt.has(key)) perAmt.set(key, new Set());
        perAmt.get(key)!.add(b.number);
      }
      for (const e of allEntries) {
        const key = Number(e.amount);
        if (!perAmt.has(key)) perAmt.set(key, new Set());
        perAmt.get(key)!.add(e.number);
      }
      for (const set of perAmt.values()) {
        if (set.size > maxSame) {
          return "Same amount limit reached for this bet amount.";
        }
      }
    }

    return null;
  }



  async function handleSubmit() {
    const err = validate();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      // Re-check server-side status (Postgres clock in Asia/Kolkata) before submitting.
      // Never trust the device clock.
      const { data: fresh, error: sErr } = await refetchStatus();
      if (sErr) throw new Error("STATUS_CHECK_FAILED: could not verify game status, please retry.");
      if (!fresh?.is_betting_open) {
        throw new Error(
          fresh?.status === "UPCOMING"
            ? "BEFORE_OPEN_TIME: Betting has not started yet."
            : "BETTING_CLOSED: Betting is closed",
        );
      }

      // One request for the whole slip (up to 100 crossing numbers), carrying a
      // stable idempotency key so a repeat tap or a network retry can never
      // create a duplicate bet or a duplicate wallet deduction.
      if (!idemKey) throw new Error("Please try again.");
      const outcome = await placeBetSlip({
        gameId,
        idempotencyKey: idemKey,
        entries: allEntries.map((e) => ({ number: e.number, amount: Number(e.amount) })),
        clientMeta: { platform: "android", submittedAtClient: new Date().toISOString() },
      });

      if (!outcome.ok) {
        if (outcome.pending) {
          toast.info(outcome.error);
          navigate({ to: "/history" });
          return;
        }
        throw new Error(outcome.error);
      }

      toast.success("Bet placed successfully.");
      await queryClient.invalidateQueries({ queryKey: ["wallet"] });
      await queryClient.invalidateQueries({ queryKey: ["profile"] });
      await queryClient.invalidateQueries({ queryKey: ["balance"] });
      await queryClient.invalidateQueries({ queryKey: ["bets_count", gameId, today] });
      await queryClient.invalidateQueries({ queryKey: ["bets_cycle_full", gameId] });
      await queryClient.invalidateQueries({ queryKey: ["bets_daily_ist", gameId] });
      clearAll();
      setIdemKey(newIdempotencyKey());
      navigate({ to: "/history" });
    } catch (e: any) {
      const msg = e?.message ?? "Failed to submit bet.";
      toast.error(msg.replace(/^.*?:\s*/, ""));
    } finally {
      setSubmitting(false);
    }
  }


  const totalCount = allEntries.length;

  return (
    <div className="flex min-h-screen flex-col bg-background pb-44">
      <div className="gradient-header px-4 pb-5 pt-3">
        <div className="flex items-center gap-3">
          <Link to="/matches" className="grid h-9 w-9 place-items-center rounded-xl bg-white/15 text-primary-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="min-w-0">
            <h1 className="truncate text-base font-black text-primary-foreground">{game?.name ?? "Play"}</h1>
            <p className="text-[11px] text-white/70">Choose bet type & enter amount</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-20 border-b bg-card px-2 py-2 shadow-card">
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted p-1">
          {(["jodi", "haruf", "crossing"] as BetTab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "rounded-lg px-2 py-2 text-xs font-black uppercase tracking-wide transition-all",
                tab === t ? "bg-primary text-primary-foreground shadow-purple" : "text-muted-foreground",
              )}
            >
              {t === "jodi" ? "Jodi" : t === "haruf" ? "Haruf" : "Crossing"} Bet
            </button>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-between px-1">
          <p className="text-[11px] text-muted-foreground">
            This bet: <span className="font-black text-primary">{totalCount}</span>
            {Number((game as any)?.max_unique_numbers ?? 0) > 0 && (() => {
              const cap = Number((game as any).max_unique_numbers);
              const used = new Set(cycleBets.map((b) => b.number)).size;
              const left = Math.max(cap - used, 0);
              return <span className="ml-2">· Numbers left: <span className="font-black text-primary">{left}</span>/{cap}</span>;
            })()}
          </p>
          {totalCount > 0 && (
            <button onClick={clearAll} className="flex items-center gap-1 rounded-lg bg-destructive/10 px-2 py-1 text-[11px] font-semibold text-destructive">
              <X className="h-3 w-3" /> Clear all
            </button>
          )}
        </div>
      </div>

      {!canBet && game && (
        <div className={cn(
          "flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wide",
          status === "CLOSED" ? "bg-destructive/15 text-destructive" : "bg-warning/15 text-warning",
        )}>
          <Lock className="h-3.5 w-3.5" />
          {status === "CLOSED" ? "Betting closed for this game." : closedMessage}
        </div>
      )}

      {canBet && activeRule && activeRule.label !== "none" && (
        <div className="border-b bg-card px-4 py-2 text-[11px] font-semibold text-primary">
          Per number ₹{activeRule.min} – ₹{activeRule.max}
        </div>
      )}
      {ruleBlocked && (
        <div className="flex items-center gap-2 bg-destructive/10 px-4 py-2 text-xs font-bold text-destructive">
          <Lock className="h-3.5 w-3.5" /> Betting is not allowed at this time.
        </div>
      )}

      {/* Body */}
      <div className="flex-1 px-3 py-4">
        {tab === "jodi" && (
          <div className="grid grid-cols-5 gap-2 sm:grid-cols-8 md:grid-cols-10">
            {Array.from({ length: 100 }, (_, i) => i).map((n) => {
              const selected = jodiSelected.has(n);
              return (
                <div key={n} className={cn("flex flex-col overflow-hidden rounded-xl shadow-card transition-all", selected ? "bg-primary/10 ring-2 ring-primary" : "bg-card")}>
                  <button
                    onClick={() => toggleJodi(n)}
                    className={cn("flex h-12 items-center justify-center text-base font-bold active:scale-95", selected ? "bg-primary text-primary-foreground" : "text-foreground")}
                  >
                    {pad2(n)}
                  </button>
                  {selected && (
                    <div className="p-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        autoFocus
                        value={jodiAmounts[n] ?? ""}
                        onChange={(e) => setJodiAmount(n, e.target.value)}
                        placeholder="₹ Amt"
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-center text-xs font-semibold outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === "haruf" && (
          <div className="space-y-4">
            <div className="rounded-xl bg-card p-3 shadow-card">
              <p className="mb-2 text-xs font-black uppercase tracking-wide">Andar (0–9)</p>
              <div className="grid grid-cols-5 gap-2 sm:grid-cols-5">
                {Array.from({ length: 10 }, (_, i) => i).map((d) => {
                  const val = andarAmounts[d] ?? "";
                  const active = val !== "" && parseFloat(val) > 0;
                  return (
                    <div key={`a${d}`} className={cn("flex flex-col overflow-hidden rounded-xl", active ? "bg-primary/10 ring-2 ring-primary" : "bg-muted")}>
                      <div className={cn("flex h-10 items-center justify-center text-base font-bold", active ? "bg-primary text-primary-foreground" : "text-foreground")}>
                        {d}
                      </div>
                      <div className="p-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={val}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v !== "" && !/^\d*\.?\d{0,2}$/.test(v)) return;
                            setAndarAmounts((p) => ({ ...p, [d]: v }));
                          }}
                          placeholder="₹ Amt"
                          className="w-full rounded-md border border-input bg-background px-1 py-1.5 text-center text-xs font-semibold outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl bg-card p-3 shadow-card">
              <p className="mb-2 text-xs font-black uppercase tracking-wide">Bahar (0–9)</p>
              <div className="grid grid-cols-5 gap-2 sm:grid-cols-5">
                {Array.from({ length: 10 }, (_, i) => i).map((d) => {
                  const val = baharAmounts[d] ?? "";
                  const active = val !== "" && parseFloat(val) > 0;
                  return (
                    <div key={`b${d}`} className={cn("flex flex-col overflow-hidden rounded-xl", active ? "bg-primary/10 ring-2 ring-primary" : "bg-muted")}>
                      <div className={cn("flex h-10 items-center justify-center text-base font-bold", active ? "bg-primary text-primary-foreground" : "text-foreground")}>
                        {d}
                      </div>
                      <div className="p-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={val}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v !== "" && !/^\d*\.?\d{0,2}$/.test(v)) return;
                            setBaharAmounts((p) => ({ ...p, [d]: v }));
                          }}
                          placeholder="₹ Amt"
                          className="w-full rounded-md border border-input bg-background px-1 py-1.5 text-center text-xs font-semibold outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {harufEntries.length > 0 && (
              <HarufPreview entries={harufEntries} />
            )}
          </div>

        )}

        {tab === "crossing" && (
          <div className="space-y-4">
            <div className="rounded-xl bg-card p-3 shadow-card">
              <p className="mb-2 text-xs font-black uppercase tracking-wide">Select digits (2–6)</p>
              <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
                {Array.from({ length: 10 }, (_, i) => i).map((d) => {
                  const sel = crossDigits.has(d);
                  return (
                    <button
                      key={`c${d}`}
                      onClick={() => toggleCross(d)}
                      className={cn("h-11 rounded-lg text-base font-bold active:scale-95", sel ? "bg-primary text-primary-foreground" : "bg-muted text-foreground")}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              <label className="mt-3 flex items-center gap-2 text-xs font-semibold">
                <input type="checkbox" checked={withJoda} onChange={(e) => setWithJoda(e.target.checked)} className="h-4 w-4 accent-primary" />
                With Joda (include 11, 22, 33, …)
              </label>
            </div>

            <div className="rounded-xl bg-card p-3 shadow-card">
              <label className="text-[11px] font-bold uppercase text-muted-foreground">Amount per generated jodi</label>
              <input
                type="text"
                inputMode="decimal"
                value={crossAmount}
                onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && setCrossAmount(e.target.value)}
                placeholder="₹ Amount"
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-base font-semibold outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            {crossingJodis.length > 0 && (
              <div className="rounded-xl bg-card p-3 shadow-card">
                <p className="mb-2 text-xs font-black uppercase tracking-wide">
                  Preview ({crossingJodis.length} jodis)
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {crossingJodis.map((n) => (
                    <span key={n} className="rounded-md bg-primary/10 px-2 py-1 text-xs font-bold text-primary">
                      {pad2(n)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="fixed inset-x-0 bottom-16 z-50 border-t bg-card/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-[480px] items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] text-muted-foreground">
              Total ({totalCount} combo{totalCount !== 1 ? "s" : ""})
            </p>
            <p className="text-lg font-black text-primary">
              ₹ {total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting || totalCount === 0 || !canBet}
            className="shrink-0 rounded-full bg-primary px-6 py-3 text-sm font-black uppercase tracking-wide text-primary-foreground shadow-purple disabled:opacity-50"
          >
            {submitting && <Loader2 className="mr-1 inline h-4 w-4 animate-spin" />}
            {!canBet ? closedMessage : submitting ? "Placing…" : "Place Bet"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HarufPreview({ entries }: { entries: { number: number; amount: number }[] }) {
  const nums = Array.from(new Set(entries.map((e) => e.number))).sort((a, b) => a - b);
  return (
    <div className="rounded-xl bg-card p-3 shadow-card">
      <p className="mb-2 text-xs font-black uppercase tracking-wide">
        Preview ({entries.length} jodis)
      </p>
      <div className="flex flex-wrap gap-1.5">
        {nums.map((n) => (
          <span key={n} className="rounded-md bg-primary/10 px-2 py-1 text-xs font-bold text-primary">
            {pad2(n)}
          </span>
        ))}
      </div>
    </div>
  );
}
